import { Bookmark, BookmarkCheck, Check, Download, Link2, MoreHorizontal, RefreshCw, Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import CompareButton from '../CompareButton';
import { tx } from '../../i18n';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { isSaved, toggleSaved } from '../../lib/saved';
import type { AnalysisDetailResponse, AnalysisSummaryResponse } from '../../types/api';

interface ReportActionsProps {
  summary: AnalysisSummaryResponse;
  detail: AnalysisDetailResponse | null;
  propertyAddressId: string | null;
  /** Re-runs every source for this address. Null while there is nothing to refresh. */
  onRefresh: (() => void) | null;
  refreshing: boolean;
}

/**
 * What to do with the report now you have read it.
 *
 * <p>One primary action and three secondary ones, which is the most a reader will weigh at a
 * glance: take it with you, keep it, put it beside another home, or send it to whoever is signing
 * with you. Every one of them has a settled state - kept, copied, in your comparison - because an
 * action that gives no answer gets pressed twice.
 */
export default function ReportActions({
  summary,
  detail,
  propertyAddressId,
  onRefresh,
  refreshing,
}: ReportActionsProps) {
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState<'idle' | 'copied' | 'failed'>('idle');
  // Only used on a phone, where four buttons of similar weight are three too many above the fold.
  // The toggle itself is hidden at wider widths, where all four fit and all four are shown.
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setSaved(isSaved(summary.analysisId));
  }, [summary.analysisId]);

  useEffect(() => {
    if (shared === 'idle') {
      return undefined;
    }
    const timer = window.setTimeout(() => setShared('idle'), 2400);
    return () => window.clearTimeout(timer);
  }, [shared]);

  const share = async () => {
    const url = window.location.href;
    const title = tx(
      `ScanHome report for ${summary.addressLine}`,
      `${summary.addressLine} için ScanHome raporu`,
      `Raport ScanHome dla ${summary.addressLine}`,
      `Relatório ScanHome de ${summary.addressLine}`,
      `Informe ScanHome de ${summary.addressLine}`,
    );
    // The share sheet is the better answer on a phone; the clipboard is the fallback everywhere
    // else. A share the reader cancels is not a failure and must not be reported as one.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          return;
        }
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared('copied');
    } catch {
      setShared('failed');
    }
  };

  return (
    <div className="report-actions" data-tour="actions">
      {detail ? (
        <a
          className="button button--block"
          href={api.pdfUrl(summary.analysisId)}
          target="_blank"
          rel="noreferrer"
        >
          <Download size={16} aria-hidden="true" />
          {tx('Download PDF', 'PDF indir', 'Pobierz PDF', 'Descarregar PDF', 'Descargar PDF')}
        </a>
      ) : null}

      <button
        type="button"
        className="button button--secondary report-actions__more"
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((value) => !value)}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
        {tx('More actions', 'Diğer işlemler', 'Więcej działań', 'Mais ações', 'Más acciones')}
      </button>

      <div className={moreOpen ? 'report-actions__secondary is-open' : 'report-actions__secondary'}>
        <div className="report-actions__row">
        <button
          type="button"
          className={saved ? 'button button--secondary button--added' : 'button button--secondary'}
          onClick={() => setSaved(toggleSaved(summary.analysisId))}
          aria-pressed={saved}
        >
          {saved ? <BookmarkCheck size={15} aria-hidden="true" /> : <Bookmark size={15} aria-hidden="true" />}
          {saved
            ? tx('Saved', 'Kaydedildi', 'Zapisane', 'Guardado', 'Guardado')
            : tx('Save property', 'Konutu kaydet', 'Zapisz mieszkanie', 'Guardar imóvel', 'Guardar vivienda')}
        </button>

        <button type="button" className="button button--secondary" onClick={() => void share()}>
          {shared === 'copied' ? <Check size={15} aria-hidden="true" /> : <Share2 size={15} aria-hidden="true" />}
          {shared === 'copied'
            ? tx('Link copied', 'Bağlantı kopyalandı', 'Skopiowano link', 'Link copiado', 'Enlace copiado')
            : tx('Share', 'Paylaş', 'Udostępnij', 'Partilhar', 'Compartir')}
        </button>
        </div>

        {shared === 'failed' ? (
          <p className="small muted report-actions__hint">
          <Link2 size={13} aria-hidden="true" />
          {tx(
            'Your browser would not let us copy the link. Copy it from the address bar instead.',
            'Tarayıcınız bağlantıyı kopyalamamıza izin vermedi. Adres çubuğundan kopyalayabilirsiniz.',
            'Przeglądarka nie pozwoliła skopiować linku. Skopiuj go z paska adresu.',
            'O seu navegador não deixou copiar o link. Copie-o da barra de endereço.',
            'Tu navegador no ha dejado copiar el enlace. Cópialo de la barra de direcciones.',
          )}
          </p>
        ) : null}

        {propertyAddressId ? (
          <CompareButton propertyAddressId={propertyAddressId} analysisId={summary.analysisId} />
        ) : null}
      </div>

      <p className="report-actions__checked small muted">
        {/* An absolute date: this line is read on a printed PDF and in a forwarded link. */}
        <span title={new Date(summary.generatedAt).toString()}>
          {tx(
            `Checked ${formatDate(summary.generatedAt)}`,
            `Kontrol: ${formatDate(summary.generatedAt)}`,
            `Sprawdzono ${formatDate(summary.generatedAt)}`,
            `Verificado em ${formatDate(summary.generatedAt)}`,
            `Comprobado el ${formatDate(summary.generatedAt)}`,
          )}
        </span>
        {onRefresh ? (
          <button type="button" className="link-button" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw size={13} aria-hidden="true" className={refreshing ? 'spin' : undefined} />
            {refreshing
              ? tx('Refreshing…', 'Yenileniyor…', 'Odświeżanie…', 'A atualizar…', 'Actualizando…')
              : tx('Refresh report', 'Raporu yenile', 'Odśwież raport', 'Atualizar relatório', 'Actualizar informe')}
          </button>
        ) : null}
      </p>

      {onRefresh ? (
        <p className="small muted">
          {tx(
            'A refresh asks the police, energy and broadband registers again.',
            'Yenileme; polis, enerji ve internet kayıtlarını yeniden sorgular.',
            'Odświeżenie ponownie odpytuje rejestry policji, energii i internetu.',
            'Atualizar volta a consultar os registos da polícia, da energia e da internet.',
            'Actualizar vuelve a consultar los registros de policía, energía e internet.',
          )}
        </p>
      ) : null}
    </div>
  );
}
